// Poverty dimension — a five-measure index table (#260).
//
// Replaces the four-bar age-band echarts chart. Aligned with the inequality
// table (#259): each measure is a row of name + (i) explainer | post-reform
// level with the change in brackets. Tom's spec (issue #260). The collapsed
// 6-panel homepage metric (poverty rate) is unaffected — it reads summary_stats.

import type { EngineOutput, PovertyMetrics } from "../../lib/types";
import { formatEur, formatEurAbbreviated } from "../../lib/formatters";
import { IndexTable, type IndexRow } from "./index_table";

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

// "+1.23" / "−1.23" with U+2212 minus, matching the rest of the UI.
const signed = (value: number, decimals: number): string =>
  `${value > 0 ? "+" : "−"}${Math.abs(value).toFixed(decimals)}`;

// Signed euros for the brackets. The formatters already prefix − for negatives,
// so only the positive sign needs adding. `abbreviated` uses "€1.2 m"/"€3.4 bn"
// for the large aggregate-gap figures.
const signedEur = (delta: number, abbreviated: boolean): string => {
  const fmt = abbreviated ? formatEurAbbreviated : formatEur;
  return delta > 0 ? `+${fmt(delta)}` : fmt(delta);
};

// Fractions shown as percentages (headcount, gap ratio): level "xx.x%", Δ in
// percentage points "+x.x pp".
const pctFmt = {
  formatLevel: (n: number) => `${(n * 100).toFixed(1)}%`,
  formatDelta: (d: number) => `${signed(d * 100, 1)} pp`,
};

export function PovertyIndices({
  engineOutput,
  baselineOutput,
  isModified,
}: Props): JSX.Element {
  const r = engineOutput.poverty_metrics;
  const b: PovertyMetrics = baselineOutput?.poverty_metrics ?? r;

  const rows: IndexRow[] = [
    {
      key: "poverty_line",
      name: "Poverty line",
      description:
        "The income cut-off that defines poverty: 60% of the median equivalised income. People in a household below it are counted as poor. It is relative, so it moves with the income distribution — neither up nor down is inherently good.",
      reform: r.threshold_reform_eur,
      baseline: b.threshold_baseline_eur,
      goodWhen: "neutral",
      formatLevel: (n) => formatEur(n),
      formatDelta: (d) => signedEur(d, false),
    },
    {
      key: "headcount",
      name: "Head count ratio",
      description:
        "Share of people who are poor — living in a household with income below the poverty line.",
      reform: r.headcount_reform,
      baseline: b.headcount_baseline,
      goodWhen: "decreasing",
      ...pctFmt,
    },
    {
      key: "aggregate_gap",
      name: "Aggregate poverty gap",
      description:
        "The total shortfall of poor households below the poverty line — the minimal sum needed to lift everyone out of poverty, scaled to the Brussels population.",
      reform: r.aggregate_poverty_gap_reform_eur,
      baseline: b.aggregate_poverty_gap_baseline_eur,
      goodWhen: "decreasing",
      formatLevel: (n) => formatEurAbbreviated(n),
      formatDelta: (d) => signedEur(d, true),
    },
    {
      key: "gap_ratio",
      name: "Poverty gap ratio",
      description:
        "How far the poor fall below the line on average, as a percentage of the line.",
      reform: r.poverty_gap_ratio_reform,
      baseline: b.poverty_gap_ratio_baseline,
      goodWhen: "decreasing",
      ...pctFmt,
    },
    {
      key: "sen",
      name: "Sen index",
      description:
        "A combined poverty measure reflecting how many are poor, how far below the line they fall, and the inequality among the poor. Higher means more severe poverty.",
      reform: r.sen_reform,
      baseline: b.sen_baseline,
      goodWhen: "decreasing",
      formatLevel: (n) => n.toFixed(3),
      formatDelta: (d) => signed(d, 3),
    },
  ];

  return (
    <div className="beamm-poverty-indices" data-testid="poverty-indices">
      <IndexTable rows={rows} isModified={isModified} testId="poverty-index-table" />
    </div>
  );
}
